import type { PersonaDto } from '@/lib/api/personas-client';

export interface PersonaTrait {
  label: string;
  /** Affinity as a multiple of the population average: an index of 230 becomes 2.3. */
  multiplier: number;
}

/**
 * An index of 100 is the population average, so only readings above it single a
 * persona out from everyone else.
 */
const POPULATION_AVERAGE_INDEX = 100;

const DEFAULT_TRAIT_COUNT = 3;

interface IndexedLabel {
  label: string;
  index: number;
}

/**
 * GWI rows and the generator's platform affinities both report on the same
 * 100-based index scale, so one normalisation serves both sources.
 */
const toTrait = ({ label, index }: IndexedLabel): PersonaTrait => ({
  label,
  multiplier: Math.round((index / POPULATION_AVERAGE_INDEX) * 100) / 100,
});

const gwiLabels = (persona: PersonaDto): IndexedLabel[] =>
  (persona.gwiStats ?? []).flatMap((section) =>
    section.rows.map((row) => ({ label: row.label, index: row.index })),
  );

const generatedPlatformLabels = (persona: PersonaDto): IndexedLabel[] =>
  (persona.socialMediaData?.data.platforms ?? []).flatMap((platform) =>
    platform.affinityIndex === undefined
      ? []
      : [{ label: platform.platformName, index: platform.affinityIndex }],
  );

/** Collapses repeats — a label can surface in more than one GWI section — on the strongest reading. */
const strongestPerLabel = (labels: IndexedLabel[]): IndexedLabel[] => {
  const strongest = new Map<string, IndexedLabel>();

  for (const entry of labels) {
    const current = strongest.get(entry.label);
    if (!current || entry.index > current.index) strongest.set(entry.label, entry);
  }

  return [...strongest.values()];
};

/**
 * The traits that set a persona apart, strongest affinity first.
 *
 * Prefers GWI stats and falls back to the generated platform affinities,
 * mirroring how the persona detail's media consumption resolves the same two
 * sources.
 *
 * @param gwiEnabled whether the `gwi_audience_linking` flag is on. When false,
 * GWI stats are ignored even if the persona carries them.
 */
export function selectDistinctiveTraits(
  persona: PersonaDto,
  gwiEnabled: boolean,
  limit: number = DEFAULT_TRAIT_COUNT,
): PersonaTrait[] {
  const gwi = gwiEnabled ? gwiLabels(persona) : [];
  const source = gwi.length > 0 ? gwi : generatedPlatformLabels(persona);

  return strongestPerLabel(source)
    .filter((entry) => entry.index > POPULATION_AVERAGE_INDEX)
    .sort((a, b) => b.index - a.index)
    .slice(0, limit)
    .map(toTrait);
}
